import { Check } from 'lucide-react';
import { cx } from './cx';
import styles from './Stepper.module.css';

export interface StepperItem<Id extends string> {
  id: Id;
  title: string;
  /** Open issues on this step; 0 shows a check; null shows no mark (nothing to judge yet). */
  issues: number | null;
}

export interface StepperProps<Id extends string> {
  /** Accessible name of the navigation landmark. */
  label: string;
  steps: StepperItem<Id>[];
  current: Id;
  onSelect: (id: Id) => void;
  /** Accessible name of a step button: its number, its name and its issue count. */
  nameOf: (step: StepperItem<Id>, position: number, total: number) => string;
  testId?: string;
  /** data-testid of each step button. */
  itemTestId?: (id: Id) => string;
  className?: string;
  /** While a task runs, no step can be opened (the batch page, WP-1b.3). Default false. */
  isDisabled?: boolean;
}

/**
 * Steps of a guided flow (DESIGN_SYSTEM §6): nav › ol › button, the current step marked with
 * aria-current="step". Any step can be opened at any time; moving never validates or saves.
 */
export function Stepper<Id extends string>({
  label,
  steps,
  current,
  onSelect,
  nameOf,
  testId,
  itemTestId,
  className,
  isDisabled = false,
}: StepperProps<Id>) {
  return (
    <nav aria-label={label} className={cx(styles.stepper, className)} data-testid={testId}>
      <ol className={styles.list}>
        {steps.map((step, index) => {
          const isCurrent = step.id === current;
          return (
            <li key={step.id} className={styles.item}>
              <button
                type="button"
                className={styles.step}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={nameOf(step, index + 1, steps.length)}
                data-testid={itemTestId?.(step.id)}
                data-issues={step.issues ?? undefined}
                disabled={isDisabled}
                onClick={() => onSelect(step.id)}
              >
                <span className={styles.number} aria-hidden="true">
                  {index + 1}
                </span>
                <span className={styles.title} aria-hidden="true">
                  {step.title}
                </span>
                {step.issues === null ? null : (
                  <span
                    className={cx(styles.state, step.issues ? styles.open : styles.clean)}
                    aria-hidden="true"
                  >
                    {step.issues ? step.issues : <Check size={13} strokeWidth={3} />}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
